<template>
    <button
        v-if="config"
        type="button"
        class="AcceptUI"
        style="display:none"
        :data-billingAddressOptions='billingAddressOptions'
        :data-apiLoginID="config.apiLoginId"
        :data-clientKey="config.publicClientKey"
        data-acceptUIFormBtnTxt="Submit"
        data-acceptUIFormHeaderTxt="Card Information"
        data-responseHandler="hostedResponseHandler"
    />
</template>

<script>
/*
 * Authorize.net's AcceptUI.js scans the DOM exactly once on script load,
 * finds elements with class="AcceptUI", and binds click handlers to them.
 * It provides no public API to re-scan or re-bind. This is fundamentally
 * incompatible with an SPA where components are destroyed and recreated
 * as users navigate — the moment Vue destroys the button's DOM element,
 * AcceptUI's binding is gone and can never be restored without a full
 * page reload.
 *
 * The solution: this component lives in AlwaysMountedComponents so the
 * hidden AcceptUI button is in the DOM for the entire session. AcceptUI
 * binds to it once and that binding survives forever. The actual visible
 * "Pay" button in HostedCheckoutForm is a proxy that just
 * programmatically clicks this hidden button when the user is ready.
 *
 * The global window.hostedResponseHandler is required by AcceptUI — it
 * calls this function by name (via the data-responseHandler attribute)
 * after the user submits their card in the hosted iframe. Because it's
 * invoked outside of Vue's context, we import the store directly and
 * commit to it rather than relying on component instance references.
 */
import { mapState } from "vuex";
import store from "@/store";

export default {
    data() {
        return {
            billingAddressOptions: '{"show":false,"required":false}',
            unsubscribeToMutations: null,
        };
    },
    created() {
        if (this.onboardingUser?.customerAuthorizeHosted) {
            this.$store.dispatch("authorizeHosted/loadConfig");
        }
    },
    mounted() {
        window.hostedResponseHandler = function (response) {
            if (response.messages.resultCode === "Error") {
                var errors = response.messages.message.map((m) => m.text);
                store.commit("authorizeHosted/setAcceptJsError", errors.join(", "));
                return;
            }

            store.commit("authorizeHosted/setAcceptJsError", null);
            store.commit("authorizeHosted/setTokenData", {
                dataDescriptor: response.opaqueData.dataDescriptor,
                dataValue: response.opaqueData.dataValue,
                bin: response.encryptedCardData.bin,
                lastFour: response.encryptedCardData.cardNumber.slice(-4),
            });
        };

        if (this.config) {
            this.$nextTick(() => {
                this.$store.dispatch("authorizeHosted/loadScript");
            });
        } else {
            this.unsubscribeToMutations = this.$store.subscribe((mutation) => {
                if (mutation.type === "authorizeHosted/setConfig" && mutation.payload) {
                    this.$nextTick(() => {
                        this.$store.dispatch("authorizeHosted/loadScript");
                    });

                    if (this.unsubscribeToMutations) {
                        this.unsubscribeToMutations();
                        this.unsubscribeToMutations = null;
                    }
                }
            });
        }
    },
    computed: {
        ...mapState("authorizeHosted", ["config"]),
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
};
</script>
